'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, X } from 'lucide-react'
import { DemoFrame, TypingDots } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'

// AI Agent Builder: the same enquiry sent to a menu chatbot and to an AI
// agent. The chatbot dead-ends; the agent understands, qualifies, books and
// writes it all to GoHighLevel. Plays the chatbot first, then switches to the
// agent on its own, until the visitor picks a side.

const OPENER = { from: 'lead', text: "Hi, do you have any openings this week? I'm comparing a few companies." }

const SCRIPTS = {
  bot: {
    messages: [
      OPENER,
      { from: 'bot', text: 'Hello! Please choose an option:\n1. Pricing\n2. Opening hours\n3. Speak to a human' },
      { from: 'lead', text: 'I just want to book a time this week.' },
      { from: 'bot', text: "Sorry, I didn't understand that. Please reply 1, 2 or 3." },
      { from: 'lead', text: 'Never mind.' },
    ],
    trail: [
      { at: 2, text: 'Answered with a menu, not an answer' },
      { at: 4, text: "Couldn't understand a normal reply" },
      { at: 5, text: 'Lead gave up and left' },
      { at: 5, text: 'Nothing saved to your CRM' },
    ],
    result: { tone: 'bad', text: 'Lead lost' },
  },
  agent: {
    messages: [
      OPENER,
      { from: 'bot', text: 'Happy to help! What would you like to sort out, so I book you with the right person?' },
      { from: 'lead', text: 'Online booking and follow-up for my dental clinic.' },
      { from: 'bot', text: 'Great fit. I have Thursday 10:00 AM or Friday 2:30 PM. Which suits you?' },
      { from: 'lead', text: 'Thursday works.' },
      { from: 'bot', text: "You're booked for Thursday at 10:00 AM. A confirmation is on its way to your phone." },
    ],
    trail: [
      { at: 2, text: 'Understood the request' },
      { at: 4, text: 'Qualified: dental clinic, booking + follow-up' },
      { at: 4, text: 'Checked the live calendar' },
      { at: 6, text: 'Booked Thursday 10:00 AM' },
      { at: 7, text: 'Contact and opportunity created in GoHighLevel' },
    ],
    result: { tone: 'good', text: 'Booked · CRM updated' },
  },
}

// Wait before each message appears (a reply waits longer: it "types" first),
// plus one last beat for the CRM step.
function delaysFor(messages) {
  return [...messages.map((m, i) => (i === 0 ? 500 : m.from === 'bot' ? 1500 : 1100)), 900]
}
const DELAYS = { bot: delaysFor(SCRIPTS.bot.messages), agent: delaysFor(SCRIPTS.agent.messages) }

export default function AgentCompare() {
  const [ref, onScreen] = useOnScreen()
  const [mode, setMode] = useState('bot')
  const [run, setRun] = useState(0)
  const [autoFlow, setAutoFlow] = useState(true)
  const script = SCRIPTS[mode]
  const step = useSequence(DELAYS[mode], { run: onScreen, resetKey: `${mode}-${run}` })
  const shown = script.messages.slice(0, Math.min(step, script.messages.length))
  const finished = step >= DELAYS[mode].length
  const typing = !finished && step < script.messages.length && script.messages[step]?.from === 'bot' && step > 0

  // The story: chatbot first, then the agent, unless the visitor took over.
  useEffect(() => {
    if (!autoFlow || mode !== 'bot' || !finished) return undefined
    const timer = setTimeout(() => setMode('agent'), 1800)
    return () => clearTimeout(timer)
  }, [autoFlow, mode, finished])

  const pick = (next) => {
    setAutoFlow(false)
    setMode(next)
    setRun((n) => n + 1)
  }

  return (
    <DemoFrame
      frameRef={ref}
      className={`svd-agent is-${mode}`}
      title="Same message, two replies"
      label="Demo: the same customer message answered by a basic chatbot and by a GHL Prime AI agent"
      tabs={[
        { key: 'bot', label: 'Basic chatbot', tone: 'bad' },
        { key: 'agent', label: 'GHL Prime AI agent', tone: 'good' },
      ]}
      activeTab={mode}
      onTab={pick}
      onReplay={() => pick(mode)}
    >
      <div className="svd-agent-grid">
        <div className="svd-chat" aria-live="polite">
          <div className="svd-chat-head">
            <span className={`svd-chat-avatar is-${mode}`}>{mode === 'agent' ? 'AI' : 'B'}</span>
            <span>
              <strong>{mode === 'agent' ? 'Your AI agent' : 'Website chatbot'}</strong>
              <small>{mode === 'agent' ? 'Replies in seconds, 24/7' : 'Menu-based bot'}</small>
            </span>
          </div>
          <div className="svd-chat-log">
            <AnimatePresence initial={false}>
              {shown.map((m, i) => (
                <motion.p
                  key={`${mode}-${run}-${i}`}
                  className={`svd-bubble is-${m.from}`}
                  initial={{ opacity: 0, y: 10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.28, ease: 'easeOut' }}
                >
                  {m.text}
                </motion.p>
              ))}
            </AnimatePresence>
            {typing ? <span className="svd-bubble is-bot is-typing"><TypingDots /></span> : null}
          </div>
        </div>

        <div className="svd-trail">
          <span className="svd-trail-title">{mode === 'agent' ? 'What the agent did' : 'What went wrong'}</span>
          <ol>
            {script.trail.map((t) => {
              const done = step >= t.at
              return (
                <li key={`${mode}-${t.text}`} className={done ? `is-done is-${script.result.tone}` : ''}>
                  <span className="svd-trail-mark" aria-hidden="true">
                    {done ? (script.result.tone === 'good' ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />) : null}
                  </span>
                  {t.text}
                </li>
              )
            })}
          </ol>
        </div>
      </div>

      <AnimatePresence>
        {finished ? (
          <motion.span
            key={`${mode}-${run}-result`}
            className={`svd-result is-${script.result.tone}`}
            initial={{ opacity: 0, y: 12, rotate: -4, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, rotate: -3, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          >
            {script.result.tone === 'good' ? <Check size={16} strokeWidth={3} /> : <X size={16} strokeWidth={3} />}
            {script.result.text}
          </motion.span>
        ) : null}
      </AnimatePresence>
    </DemoFrame>
  )
}
